import { AnimatePresence, motion } from 'motion/react'
import { useRef, useState } from 'react'
import { cn } from '@/utils/styles/cn'

interface IMarketingVideoProps {
    isPlaying?: boolean
    setRef?: (ref: HTMLVideoElement) => void
    onEnded?: () => void
    onPlayToggle?: () => void
    src: string
    aspectRatio: number
    title?: string
    description?: string
    videoClassName?: string
    className?: string
    thumbnail?: string
    ariaLabelDescription?: string
}

export const MarketingVideo = ({
    isPlaying,
    onPlayToggle,
    description,
    title,
    setRef,
    src,
    onEnded,
    aspectRatio,
    videoClassName,
    className,
    thumbnail,
    ariaLabelDescription,
}: IMarketingVideoProps) => {
    const [internalIsPlaying, setInternalIsPlaying] = useState(false)

    const internalRef = useRef<HTMLVideoElement | null>(null)

    const internalOnPlayToggle = () => {
        if (internalRef.current?.paused) {
            internalRef.current?.play()
            setInternalIsPlaying(true)
        } else {
            internalRef.current?.pause()
            setInternalIsPlaying(false)
        }
    }

    const internalOnEnded = () => {
        internalRef.current?.pause()
        setInternalIsPlaying(false)
    }

    return (
        <div
            className={cn(
                'relative rounded-[32px] overflow-hidden cursor-pointer bg-black/10',
                className
            )}
            style={{ aspectRatio }}
            onClick={onPlayToggle ?? internalOnPlayToggle}
        >
            <AnimatePresence initial={false}>
                {(isPlaying === undefined ? !internalIsPlaying : !isPlaying) && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        id="layover"
                        className={cn(
                            `absolute select-none w-full h-full bg-gradient-to-b from-[rgba(17,48,29,0)] from-50% to-[#11301D] to-100% top-0 left-0 z-[1] flex items-end
                            py-8 px-6`
                        )}
                    >
                        <motion.div
                            initial={{ opacity: 0, x: '-50%', y: '-50%' }}
                            animate={{ opacity: 1, x: '-50%', y: '-50%' }}
                            exit={{ opacity: 0, x: '-50%', y: '-50%' }}
                            whileHover={{ scale: 1.1, opacity: 0.9 }}
                            whileTap={{ scale: 0.95 }}
                            className="absolute top-1/2 left-1/2"
                        >
                            <svg
                                width="64"
                                height="64"
                                viewBox="0 0 64 64"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect width="64" height="64" rx="12" fill="#FEFEFE" />
                                <path d="M29 40L31 40L39 32L31 24L29 24L29 40Z" fill="#11301D" />
                            </svg>
                        </motion.div>

                        <div className="flex flex-col">
                            {title && <p className="text-white font-bold text-[28px]">{title}</p>}

                            {description && <p className="text-white/70">{description}</p>}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <video
                aria-label={ariaLabelDescription}
                playsInline
                ref={(ref) => {
                    if (ref) {
                        if (setRef) setRef(ref)
                        if (!setRef) internalRef.current = ref
                    }
                }}
                onEnded={onEnded ?? internalOnEnded}
                controls={false}
                poster={thumbnail}
                preload="metadata"
                src={src}
                style={{ aspectRatio }}
                onClick={(e) => {
                    e.stopPropagation()

                    if (onPlayToggle) onPlayToggle()
                    if (!onPlayToggle) internalOnPlayToggle()
                }}
                className={cn('h-full object-cover w-full rounded-lg', videoClassName)}
            />
        </div>
    )
}
